// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Analysis-result view components — no behavior change.
import React from 'react';
import { MolecularMarkerScanBanner } from '@/components/results/MolecularMarkerScanBanner';

export function NonParametricView({ data }) {
  if (!data) return null;

  const kw = data.kruskal_wallis || [];
  const kwDunn = data.kruskal_dunn || {};
  const kwCld = data.kruskal_cld || {};
  const mw = data.mann_whitney || [];
  const fr = data.friedman || [];
  const frNem = data.friedman_nemenyi || {};
  const sp = data.spearman_results || [];
  const wlx = data.wilcoxon || [];
  const charts = data.charts || [];

  const thCls = 'px-3 py-2 text-center font-semibold border border-[#B45309]/30';
  const tdCls = 'px-3 py-1.5 text-center font-mono border border-[#E8E6E1]';
  const tdLCls = 'px-3 py-1.5 font-semibold border border-[#E8E6E1]';
  const fmtP = (p) => {
    if (p === null || p === undefined) return '-';
    if (typeof p === 'string') return p;
    if (p < 0.001) return '<0.001';
    return p.toFixed(4);
  };

  return (
    <div className="space-y-8" data-testid="non-parametric-view">
      {data.interpretation && (
        <div className="bg-[#FEF3E2] rounded-2xl p-5 border border-[#B45309]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed whitespace-pre-line">{data.interpretation}</p>
        </div>
      )}

      {/* 1. Kruskal-Wallis */}
      {kw.length > 0 && (
        <div data-testid="np-kruskal-wallis">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Kruskal-Wallis Test</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#B45309] text-white">
                {['Trait', 'H Statistic', 'df', 'P-value', 'Conclusion'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {kw.map((row, i) => (
                  <tr key={row.Trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FFF8F0]'}>
                    <td className={tdLCls}>{row.Trait}</td>
                    <td className={tdCls}>{row.H}</td>
                    <td className={tdCls}>{row.df}</td>
                    <td className={tdCls}>{fmtP(row.P)}</td>
                    <td className="px-3 py-1.5 text-xs border border-[#E8E6E1]">{row.Conclusion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Dunn post-hoc with CLD */}
          {Object.keys(kwCld).length > 0 && (
            <div className="mt-4 space-y-3">
              <h5 className="text-xs font-semibold text-[#5E5A56] uppercase tracking-wider">Post-hoc Dunn Test — Significance Letters</h5>
              {Object.entries(kwCld).map(([trait, cld]) => (
                <div key={trait} className="flex flex-wrap gap-2 items-center">
                  <span className="text-xs font-semibold text-[#2C2A28] min-w-[80px]">{trait}:</span>
                  {Object.entries(cld).map(([grp, letters]) => (
                    <span key={grp} className="inline-flex items-center gap-1 px-2 py-1 bg-[#FEF3E2] rounded-lg border border-[#B45309]/20 text-xs">
                      <span className="font-mono">{grp}</span>
                      <span className="font-bold text-[#C0392B]">{letters}</span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 2. Mann-Whitney U */}
      {mw.length > 0 && (
        <div data-testid="np-mann-whitney">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Mann-Whitney U Test (Pairwise)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#B45309] text-white">
                {['Trait', 'Group 1', 'Group 2', 'U Statistic', 'P-value', 'Effect Size (r)', 'Conclusion'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {mw.map((row, i) => (
                  <tr key={`${row.Trait}-${row.Group1}-${row.Group2}`} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FFF8F0]'}>
                    <td className={tdLCls}>{row.Trait}</td>
                    <td className={tdCls}>{row.Group1}</td>
                    <td className={tdCls}>{row.Group2}</td>
                    <td className={tdCls}>{row.U}</td>
                    <td className={tdCls}>{fmtP(row.P)}</td>
                    <td className={`${tdCls} ${row.Effect_r > 0.5 ? 'font-bold text-red-600' : row.Effect_r > 0.3 ? 'text-amber-600' : ''}`}>
                      {row.Effect_r.toFixed(4)}
                    </td>
                    <td className="px-3 py-1.5 text-xs border border-[#E8E6E1]">{row.Conclusion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 3. Friedman Test */}
      {fr.length > 0 && (
        <div data-testid="np-friedman">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Friedman Test</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#B45309] text-white">
                {['Trait', 'Chi-Squared', 'df', 'P-value', 'Conclusion'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {fr.map((row, i) => (
                  <tr key={row.Trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FFF8F0]'}>
                    <td className={tdLCls}>{row.Trait}</td>
                    <td className={tdCls}>{row.Chi2}</td>
                    <td className={tdCls}>{row.df}</td>
                    <td className={tdCls}>{fmtP(row.P)}</td>
                    <td className="px-3 py-1.5 text-xs border border-[#E8E6E1]">{row.Conclusion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Nemenyi post-hoc */}
          {Object.keys(frNem).length > 0 && (
            <div className="mt-4 space-y-3">
              <h5 className="text-xs font-semibold text-[#5E5A56] uppercase tracking-wider">Post-hoc Nemenyi Test</h5>
              {Object.entries(frNem).map(([trait, pairs]) => (
                <div key={trait}>
                  <span className="text-xs font-semibold text-[#2C2A28]">{trait}:</span>
                  <div className="overflow-x-auto mt-1">
                    <table className="text-xs border-collapse">
                      <thead><tr className="bg-[#D97706]/20">
                        {['Group 1', 'Group 2', 'Rank Diff', 'Adj. P-value', 'Sig.'].map(h => (
                          <th key={h} className="px-2 py-1 text-center font-semibold border border-[#E8E6E1]">{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {pairs.map((pr, pi) => (
                          <tr key={pi} className={pr.sig ? 'bg-red-50' : ''}>
                            <td className="px-2 py-1 border border-[#E8E6E1] font-mono">{pr.group1}</td>
                            <td className="px-2 py-1 border border-[#E8E6E1] font-mono">{pr.group2}</td>
                            <td className="px-2 py-1 text-center border border-[#E8E6E1] font-mono">{pr.diff_rank}</td>
                            <td className="px-2 py-1 text-center border border-[#E8E6E1] font-mono">{fmtP(pr.p_adj)}</td>
                            <td className={`px-2 py-1 text-center border border-[#E8E6E1] font-bold ${pr.sig ? 'text-red-600' : 'text-green-600'}`}>
                              {pr.sig ? 'Yes' : 'No'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 4. Spearman Rank Correlation */}
      {sp.length > 0 && (
        <div data-testid="np-spearman">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Spearman Rank Correlation</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#B45309] text-white">
                {['Variable 1', 'Variable 2', 'Rho', 't-statistic', 'P-value', 'Interpretation'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {sp.map((row, i) => (
                  <tr key={`${row.Var1}-${row.Var2}`} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FFF8F0]'}>
                    <td className={tdLCls}>{row.Var1}</td>
                    <td className={tdLCls}>{row.Var2}</td>
                    <td className={`${tdCls} ${row.Rho > 0 ? 'text-emerald-700' : 'text-red-600'} font-bold`}>
                      {row.Rho.toFixed(4)}
                    </td>
                    <td className={tdCls}>{row.t_stat.toFixed(4)}</td>
                    <td className={tdCls}>{fmtP(row.P)}</td>
                    <td className="px-3 py-1.5 text-xs border border-[#E8E6E1]">{row.Interpretation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 5. Wilcoxon Signed-Rank */}
      {wlx.length > 0 && (
        <div data-testid="np-wilcoxon">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Wilcoxon Signed-Rank Test</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#B45309] text-white">
                {['Trait', 'Group 1', 'Group 2', 'W Statistic', 'P-value', 'Effect Size (r)', 'Conclusion'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {wlx.map((row, i) => (
                  <tr key={`${row.Trait}-${row.Group1}-${row.Group2}`} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FFF8F0]'}>
                    <td className={tdLCls}>{row.Trait}</td>
                    <td className={tdCls}>{row.Group1}</td>
                    <td className={tdCls}>{row.Group2}</td>
                    <td className={tdCls}>{row.W}</td>
                    <td className={tdCls}>{fmtP(row.P)}</td>
                    <td className={`${tdCls} ${row.Effect_r > 0.5 ? 'font-bold text-red-600' : row.Effect_r > 0.3 ? 'text-amber-600' : ''}`}>
                      {row.Effect_r.toFixed(4)}
                    </td>
                    <td className="px-3 py-1.5 text-xs border border-[#E8E6E1]">{row.Conclusion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!data.has_blocks && (
        <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
          <p className="text-xs text-amber-800 italic">Friedman and Wilcoxon Signed-Rank tests require Block/Rep column. Upload data with blocks to enable these tests.</p>
        </div>
      )}

      {/* Charts */}
      {charts.length > 0 && (
        <div className="space-y-6" data-testid="np-charts">
          {charts.map((c, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#E8E6E1] p-4 shadow-sm" data-testid={`np-chart-${i}`}>
              <img src={`data:image/png;base64,${c}`} alt={['Kruskal-Wallis Box Plots', 'Spearman Correlation Heatmap'][i] || `Chart ${i+1}`} className="w-full max-w-[800px] mx-auto rounded-lg" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ============================================================
   DIVERSITY ANALYSIS VIEW
============================================================ */


export function DiversityView({ data }) {
  if (!data) return null;

  const mStats = data.marker_stats || [];
  const popDiv = data.pop_diversity || [];
  const neiDist = data.nei_distance || [];
  const neiLabels = data.nei_labels || [];
  const amova = data.amova || [];
  const pFst = data.pairwise_fst || [];
  const clusters = data.cluster_membership || [];
  const nClusters = data.n_clusters || 0;

  const thCls = 'px-2 py-2 text-center font-semibold border border-[#059669]/30 text-xs';
  const tdCls = 'px-2 py-1.5 text-center font-mono border border-[#E8E6E1] text-xs';
  const tdLCls = 'px-2 py-1.5 font-semibold border border-[#E8E6E1] text-xs';
  const fmtP = (p) => {
    if (p === null || p === undefined || p === '-') return p || '-';
    if (typeof p === 'string') return p;
    if (p < 0.001) return '<0.001';
    return p.toFixed(4);
  };

  const qCols = nClusters > 0
    ? Array.from({ length: nClusters }, (_, i) => `Q_Cluster${i + 1}`)
    : [];

  return (
    <div className="space-y-8" data-testid="diversity-view">
      {data.interpretation && (
        <div className="bg-[#ECFDF5] rounded-2xl p-5 border border-[#059669]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed whitespace-pre-line">{data.interpretation}</p>
        </div>
      )}

      {/* Marker-type validation banner — surfaces inferred marker
          types and the pipeline assumption before the user reads any
          downstream stats (Pattern 3 + Pattern 10). */}
      {data.marker_type_scan && (
        <MolecularMarkerScanBanner scan={data.marker_type_scan} testId="diversity-marker-type-scan" />
      )}

      {/* 1. Marker Statistics Table */}
      {mStats.length > 0 && (
        <div data-testid="div-marker-stats">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">
            Marker Statistics ({data.n_markers_kept}/{data.n_markers_total} retained, MAF &ge; 0.05)
          </h4>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0"><tr className="bg-[#059669] text-white">
                {['Marker', 'N Alleles', 'MAF', 'PIC', 'Ho', 'He'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {mStats.map((row, i) => (
                  <tr key={row.Marker} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FDF4]'}>
                    <td className={tdLCls}>{row.Marker}</td>
                    <td className={tdCls}>{row.N_Alleles}</td>
                    <td className={tdCls}>{row.MAF?.toFixed(4) ?? '-'}</td>
                    <td className={`${tdCls} ${(row.PIC ?? 0) > 0.5 ? 'font-bold text-emerald-700' : (row.PIC ?? 0) > 0.25 ? 'text-amber-600' : ''}`}>
                      {row.PIC?.toFixed(4) ?? '-'}
                    </td>
                    <td className={tdCls}>{row.Ho?.toFixed(4) ?? '-'}</td>
                    <td className={tdCls}>{row.He?.toFixed(4) ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 2. Population Genetic Diversity Table */}
      {popDiv.length > 0 && (
        <div data-testid="div-pop-diversity">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Population Genetic Diversity</h4>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead><tr className="bg-[#059669] text-white">
                {['Population', 'N', 'Na', 'Ne', 'Ho', 'He', 'Fis', 'Shannon (I)'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {popDiv.map((row, i) => (
                  <tr key={row.Population} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FDF4]'}>
                    <td className={tdLCls}>{row.Population}</td>
                    <td className={tdCls}>{row.N}</td>
                    <td className={tdCls}>{row.Na?.toFixed(4) ?? '-'}</td>
                    <td className={tdCls}>{row.Ne?.toFixed(4) ?? '-'}</td>
                    <td className={tdCls}>{row.Ho?.toFixed(4) ?? '-'}</td>
                    <td className={tdCls}>{row.He?.toFixed(4) ?? '-'}</td>
                    <td className={`${tdCls} ${(row.Fis ?? 0) > 0.15 ? 'font-bold text-red-600' : (row.Fis ?? 0) > 0.05 ? 'text-amber-600' : ''}`}>
                      {row.Fis?.toFixed(4) ?? '-'}
                    </td>
                    <td className={tdCls}>{row.Shannon?.toFixed(4) ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 3. Pairwise Genetic Distance Matrix (Nei's) */}
      {neiDist.length > 0 && (
        <div data-testid="div-nei-distance">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Pairwise Nei&apos;s Genetic Distance</h4>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="border-collapse text-[10px]">
              <thead className="sticky top-0"><tr className="bg-[#059669] text-white">
                <th className="px-1 py-1 border border-[#059669]/30 font-semibold sticky left-0 bg-[#059669] z-10">ID</th>
                {neiLabels.map(l => <th key={l} className="px-1 py-1 border border-[#059669]/30 font-semibold">{l}</th>)}
              </tr></thead>
              <tbody>
                {neiDist.map((row, i) => (
                  <tr key={row.Genotype}>
                    <td className="px-1 py-1 font-semibold border border-[#E8E6E1] sticky left-0 bg-white z-10">{row.Genotype}</td>
                    {neiLabels.map((l, j) => {
                      const val = row[l] || 0;
                      const bg = i === j ? 'bg-gray-200' : val > 0.3 ? 'bg-red-100' : val > 0.1 ? 'bg-amber-50' : 'bg-emerald-50';
                      return <td key={l} className={`px-1 py-1 text-center font-mono border border-[#E8E6E1] ${bg}`}>{i === j ? '-' : val.toFixed(4)}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 4. AMOVA Table */}
      {amova.length > 0 && (
        <div data-testid="div-amova">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">AMOVA (Analysis of Molecular Variance)</h4>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead><tr className="bg-[#059669] text-white">
                {['Source', 'df', 'SS', 'MS', '% Variation', 'Fst', 'P-value'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {amova.map((row, i) => (
                  <tr key={row.Source} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FDF4]'}>
                    <td className={tdLCls}>{row.Source}</td>
                    <td className={tdCls}>{row.df}</td>
                    <td className={tdCls}>{typeof row.SS === 'number' ? row.SS.toFixed(4) : row.SS}</td>
                    <td className={tdCls}>{typeof row.MS === 'number' ? row.MS.toFixed(6) : row.MS}</td>
                    <td className={tdCls}>{typeof row.Pct_Variation === 'number' ? row.Pct_Variation.toFixed(2) + '%' : row.Pct_Variation}</td>
                    <td className={`${tdCls} font-bold`}>{typeof row.Fst === 'number' ? row.Fst.toFixed(4) : row.Fst}</td>
                    <td className={tdCls}>{fmtP(row.P)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.amova_footnote && (
            <p className="mt-2 text-xs text-[#5E5A56] italic" data-testid="div-amova-footnote">{data.amova_footnote}</p>
          )}
        </div>
      )}

      {/* 5. Pairwise Fst Table */}
      {pFst.length > 0 && (
        <div data-testid="div-pairwise-fst">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Pairwise Fst</h4>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead><tr className="bg-[#059669] text-white">
                {['Population 1', 'Population 2', 'Fst', 'P-value'].map(h => (
                  <th key={h} className={thCls}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {pFst.map((row, i) => (
                  <tr key={`${row.Pop1}-${row.Pop2}`} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FDF4]'}>
                    <td className={tdLCls}>{row.Pop1}</td>
                    <td className={tdLCls}>{row.Pop2}</td>
                    <td className={`${tdCls} ${row.Fst > 0.15 ? 'font-bold text-red-600' : row.Fst > 0.05 ? 'text-amber-600' : 'text-emerald-700'}`}>
                      {row.Fst.toFixed(4)}
                    </td>
                    <td className={tdCls}>{fmtP(row.P)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 6. Cluster Membership Table */}
      {clusters.length > 0 && (
        <div data-testid="div-cluster-membership">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Cluster Membership (K={nClusters})</h4>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0"><tr className="bg-[#059669] text-white">
                <th className={thCls}>Genotype</th>
                <th className={thCls}>Cluster</th>
                {qCols.map(q => <th key={q} className={thCls}>{q.replace('Q_', 'Q ')}</th>)}
              </tr></thead>
              <tbody>
                {clusters.map((row, i) => {
                  const clusterColors = ['bg-emerald-100', 'bg-sky-100', 'bg-amber-100', 'bg-rose-100', 'bg-violet-100', 'bg-teal-100'];
                  return (
                    <tr key={row.Genotype} className={clusterColors[(row.Cluster - 1) % clusterColors.length]}>
                      <td className={tdLCls}>{row.Genotype}</td>
                      <td className={`${tdCls} font-bold`}>{row.Cluster}</td>
                      {qCols.map(q => {
                        const val = row[q] || 0;
                        return <td key={q} className={`${tdCls} ${val > 0.5 ? 'font-bold text-emerald-700' : ''}`}>{val.toFixed(4)}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 7. Diversity Analysis Figures */}
      {data.charts && data.charts.length > 0 && (
        <div className="space-y-10 mt-10 pt-8 border-t-2 border-[#059669]/20" data-testid="diversity-charts">
          <h3 className="font-bold text-[#2C2A28] text-base uppercase tracking-wider">Figures</h3>
          {data.charts.map((chart, idx) => (
            <div key={idx} className="bg-white rounded-2xl border border-[#E8E6E1] p-5 shadow-sm" data-testid={`diversity-figure-${idx + 1}`}>
              <h4 className="font-semibold text-[#2C2A28] mb-4 text-sm">
                {data.chart_titles?.[idx] || `Figure ${idx + 1}`}
              </h4>
              <img
                src={`data:image/png;base64,${chart}`}
                alt={data.chart_titles?.[idx] || `Figure ${idx + 1}`}
                className="w-full rounded-lg"
              />
              {data.chart_captions?.[idx] && (
                <p className="mt-3 text-xs text-[#5E5A56] italic leading-relaxed">{data.chart_captions[idx]}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ============================================================
   AUGMENTED MET VIEW (Sub-Batch 3T — Scenario A + Scenario-B refusal)
============================================================ */




/* ============================================================
   GENERIC ANOVA + TABLE VIEW (for CRD, RBD, etc.)
============================================================ */
